import React, { useState, useEffect } from 'react';
import { Horse, BoxCell } from './types';
import { loadHorsesData } from './horsesData';
import { generateBoxPlanPDF } from './boxPlanPDF';
import './BoxPlanView.css';

interface BoxPlanViewProps {
  onNavigate?: (view: string) => void;
}

const BoxPlanView: React.FC<BoxPlanViewProps> = ({ onNavigate }) => {
  const [horses, setHorses] = useState<Horse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [ownerNumberMap, setOwnerNumberMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { horses } = await loadHorsesData();
      setHorses(horses);
      const ownerMap = createOwnerNumberMap(horses);
      setOwnerNumberMap(ownerMap);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Fehler beim Laden der Daten');
      setIsLoading(false);
    }
  };

  // Determine if a horse should be visible based on hidden status and contract end date
  const isHorseVisible = (horse: Horse): boolean => {
    // If not hidden, always visible
    if (!horse.hidden) return true;

    // If hidden, check if contract end date is in current month or future
    if (horse.contract?.end_date) {
      const endDate = new Date(horse.contract.end_date);
      const today = new Date();

      // Get year-month for comparison (year * 12 + month gives comparable values)
      const endYearMonth = endDate.getFullYear() * 12 + endDate.getMonth();
      const currentYearMonth = today.getFullYear() * 12 + today.getMonth();

      // Visible if contract ends in current month or later
      return endYearMonth >= currentYearMonth;
    }

    // If hidden and no end date, hide it
    return false;
  };

  const createOwnerNumberMap = (horses: Horse[]): Map<string, string> => {
    // Erstelle Map: owner_name -> Besitzernummer (1-18)
    const uniqueOwners = new Set<string>();
    horses.forEach(h => {
      if (h.contract?.owner_name && isHorseVisible(h)) {
        uniqueOwners.add(h.contract.owner_name);
      }
    });

    const sortedOwners = Array.from(uniqueOwners).sort();
    const map = new Map<string, string>();
    sortedOwners.forEach((owner, index) => {
      map.set(owner, String(index + 1));
    });

    return map;
  };

  const getHorseByBoxNumber = (boxNumber: string): Horse | undefined => {
    return horses.find(h => h.boxNumber === boxNumber && isHorseVisible(h));
  };

  const getHorsesByBoxNumber = (boxNumber: string): Horse[] => {
    return horses.filter(h => h.boxNumber === boxNumber && isHorseVisible(h));
  };

  const handleBoxClick = (boxNumber: string) => {
    if (!onNavigate) return;

    const boxHorses = getHorsesByBoxNumber(boxNumber);

    if (boxHorses.length > 0) {
      // Box is occupied - show info and navigate to horse editor
      const horseNames = boxHorses.map(h => h.name).join(', ');
      if (window.confirm(
        `Box ${boxNumber} ist belegt mit:\n${horseNames}\n\n` +
        `Möchten Sie zum Pferde-Editor wechseln?`
      )) {
        onNavigate('editor');
      }
    } else {
      // Box is empty - navigate to contract editor
      if (window.confirm(
        `Box ${boxNumber} ist leer.\n\n` +
        `Möchten Sie einen neuen Vertrag erstellen?`
      )) {
        onNavigate('contract');
      }
    }
  };

  const handleFoodClick = () => {
    alert('Futterverwaltung\n\nDiese Funktion wird in Kürze verfügbar sein.\nHier können Sie dann Futtermittel und Futterpläne verwalten.');
  };

  const renderBox = (boxNumber: string, label?: string) => {
    const boxHorses = getHorsesByBoxNumber(boxNumber);
    const hasHorses = boxHorses.length > 0;

    // Special classes for O boxes
    let boxClass = hasHorses ? 'box-occupied' : 'box-empty';
    if (boxNumber === 'O1' || boxNumber === 'O2') {
      boxClass = 'box-offenstall';
    } else if (boxNumber === 'O3' || boxNumber === 'O4') {
      boxClass = 'box-lamastall';
    }

    return (
      <div
        key={boxNumber}
        className={`box-cell ${boxClass} ${onNavigate ? 'clickable' : ''}`}
        data-box={boxNumber}
        onClick={() => handleBoxClick(boxNumber)}
        style={{ cursor: onNavigate ? 'pointer' : 'default' }}
      >
        {hasHorses ? (
          <>
            {boxHorses.map((horse, index) => {
              const ownerNumber = horse.contract?.owner_name ? ownerNumberMap.get(horse.contract.owner_name) : undefined;
              return (
                <div key={horse.id} className={`horse-entry${horse.absent ? ' horse-absent' : ''}`}>
                  {index > 0 && <span className="horse-separator">, </span>}
                  <span className="owner-number">{ownerNumber}</span>
                  <span className="horse-name">{horse.name}{horse.absent ? ' *' : ''}</span>
                </div>
              );
            })}
          </>
        ) : (
          <span className="box-label">{label || boxNumber}</span>
        )}
      </div>
    );
  };

  const handlePrintPDF = () => {
    const boxLayout = createBoxLayout();
    generateBoxPlanPDF(horses, boxLayout, ownerNumberMap);
  };

  const createBoxLayout = (): BoxCell[][] => {
    // New layout with updated box numbering
    const boxes: BoxCell[] = [];

    // All box numbers in the new layout
    const boxNumbers = [
      // Top row
      'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'A11', 'A12',
      // Left and middle sections
      'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'B10', 'B11',
      // Right section
      'C1', 'C2', 'C3', 'C4',
      // D column
      'D1', 'D2',
      // Offenstall
      'O1', 'O2', 'O3', 'O4'
    ];

    boxNumbers.forEach(boxNum => {
      const horse = getHorseByBoxNumber(boxNum);
      boxes.push({
        boxNumber: boxNum,
        horseName: horse?.name,
        ownerNumber: horse?.contract?.owner_name ? ownerNumberMap.get(horse.contract.owner_name) : undefined,
        ownerName: horse?.contract?.owner_name,
        status: horse ? 'occupied' : 'empty',
        type: 'box'
      });
    });

    return [boxes]; // Single row for now, will be styled with CSS Grid
  };

  const generateOwnerLegend = () => {
    const legend: Array<{ number: string; name: string }> = [];
    ownerNumberMap.forEach((number, name) => {
      legend.push({ number, name });
    });

    return legend.sort((a, b) => parseInt(a.number) - parseInt(b.number));
  };

  if (isLoading) {
    return (
      <div className="box-plan-view">
        <div className="loading">Lade Boxenplan...</div>
      </div>
    );
  }

  const ownerLegend = generateOwnerLegend();

  return (
    <div className="box-plan-view">
      <header className="box-plan-header">
        <div>
          <h2>Boxenbelegung</h2>
          <p>Stallplan {new Date().getFullYear()}</p>
        </div>
        <button onClick={handlePrintPDF} className="btn-print">
          📄 PDF Drucken
        </button>
      </header>

      <div className="box-plan-container">
        <div className="stable-layout">
          <div className="note-text">
            Die Nummer vor dem Pferdenamen ist der Besitzer
          </div>

          <div className="stable-grid">
            {/* Top Row: A1-A12 */}
            {renderBox('A1')}
            {renderBox('A2')}
            {renderBox('A3')}
            {renderBox('A4')}
            {renderBox('A5')}
            {renderBox('A6')}
            {renderBox('A7')}
            {renderBox('A8')}
            {renderBox('A9')}
            {renderBox('A10')}
            {renderBox('A11')}
            {renderBox('A12')}

            {/* Left Section: B1-B4 (top row) */}
            {renderBox('B1')}
            {renderBox('B2')}
            {renderBox('B3')}
            {renderBox('B4')}

            {/* Large B8 box (6x6) */}
            {renderBox('B8')}

            {/* B9, B10 stack */}
            {renderBox('B9')}
            {renderBox('B10')}

            {/* B11 tall box (3x6) */}
            {renderBox('B11')}

            {/* Middle column: B5, B6, B7 (3.5 wide each) */}
            {renderBox('B5')}
            {renderBox('B6')}
            {renderBox('B7')}

            {/* Stairs + D1 + D2 column */}
            <div key="stairs" className="box-cell box-special box-stair" data-box="STAIRS">
              <span className="special-label">Treppe</span>
            </div>
            {renderBox('D1')}
            {renderBox('D2')}

            {/* Right section: C1-C4 */}
            {renderBox('C1')}
            {renderBox('C2')}
            {renderBox('C3')}
            {renderBox('C4')}

            {/* Food storage */}
            <div
              key="food"
              className="box-cell box-special box-feed clickable"
              data-box="FOOD"
              onClick={handleFoodClick}
              style={{ cursor: 'pointer' }}
            >
              <span className="special-label">Futtermittel</span>
            </div>

            {/* Offenstall boxes: O1-O4 */}
            {renderBox('O1')}
            {renderBox('O2')}
            {renderBox('O3')}
            {renderBox('O4')}
          </div>

          <div className="note-footer">
            Änderungen auch kurzfristig möglich
          </div>
        </div>

        <div className="legend-section">
          <h3>Besitzer</h3>
          <div className="legend-grid">
            {ownerLegend.map(({ number, name }) => (
              <React.Fragment key={number}>
                <span className="legend-number">{number}</span>
                <span className="legend-name">{name}</span>
              </React.Fragment>
            ))}
          </div>

          <div className="color-legend">
            <h3>Legende</h3>
            <div className="legend-item">
              <span className="legend-color legend-color-occupied"></span>
              <span>Einsteller</span>
            </div>
            <div className="legend-item">
              <span className="legend-color legend-color-empty"></span>
              <span>Leere Box</span>
            </div>
            <div className="legend-item">
              <span className="legend-color legend-color-special"></span>
              <span>Sonderbereiche</span>
            </div>
            <div className="legend-item">
              <span className="legend-color legend-color-offenstall"></span>
              <span>Offenstall (O1/O2)</span>
            </div>
            <div className="legend-item">
              <span className="legend-color legend-color-lamastall"></span>
              <span>Lamastall (O3/O4)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoxPlanView;
